<template>
  <div name="step4">
    <!-- step bar -->
    <StepBox :step="step" :exam="exam" />

    <!-- tab nav -->
    <div class="tab-box">
      <span
        class="item-1"
        v-if="getExam.frontMonitoring || getExam.faceEvent"
        :class="{active: isWebcamPhase}">
        <div class="inner">
          <strong>{{ $t('preRegistration.step4.front.title') }}</strong>
          <p>{{ $t('preRegistration.step4.front.type') }}</p>
        </div>
      </span>
      <span
        class="item-2"
        v-if="getExam.sideMonitoring"
        :class="{active: isMobilePhase}">
        <div class="inner">
          <strong>{{ $t('preRegistration.step4.side.title') }}</strong>
          <p>{{ $t('preRegistration.step4.side.type') }}</p>
        </div>
      </span>
      <span
        class="item-3"
        v-if="getExam.screenMonitoring"
        :class="{active: isScreenPhase}">
        <div class="inner">
          <strong>{{ $t('preRegistration.step4.screen.title') }}</strong>
          <p>{{ $t('preRegistration.step4.screen.type') }}</p>
        </div>
      </span>
    </div>
    <!-- front -->
    <Front
      :userId="userInfo.id"
      :roomId="periodInfo.videoRoomId"
      :phase="phase"
      :active="webcamIsRunning"
      :done="connectChecked['webcam']"
      :frontMonitoring="getExam.frontMonitoring"
      :faceEvent="getExam.faceEvent"
      @connectStatusChanged="connectStatusChanged"
      @changeFrontActive="changeFrontActive" />
    <!-- side -->
    <Mobile
      :userId="userInfo.id"
      :roomId="periodInfo.videoRoomId"
      :phase="phase"
      :active="mobileIsRunning"
      :done="connectChecked['mobile']"
      :sideMonitoring="getExam.sideMonitoring"
      @connectStatusChanged="connectStatusChanged"
      @changeMobileActive="changeMobileActive" />

    <!-- screen -->
    <Screen
      :userId="userInfo.id"
      :roomId="periodInfo.videoRoomId"
      :phase="phase"
      :active="screenIsRunning"
      :done="connectChecked['screen']"
      :screenMonitoring="getExam.screenMonitoring"
      @connectStatusChanged="connectStatusChanged"
      @changeScreenActive="changeScreenActive" />

    <!-- buttons -->
    <div class="btn-box step">
      <div class="col">
        <button
          type="button"
          class="btn btn-line-dark btn-lg icon icon-arrow_prev left"
          :disabled="isPreviousDisabled"
          @click="previous"
          name="go-back-step-3">
          {{ $t('preRegistration.common.previousStep') }}
        </button>
      </div>
      <div class="col ml-auto">
        <button
          type="button"
          class="btn btn-line-dark btn-lg icon icon-arrow_next"
          :disabled="isNextDisabled"
          @click="onNext(phase)"
          :name="phase === 1 ? labelButton : `go-to-${phase}-check`">
          {{ labelButton }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex';
  import {
    DEVICE_TYPE,
    EXAM_RENDER_TYPE,
    CONNECT_CHECK_PHASE,
  } from '@/js/common/constants';
  import stepMixin from '@/mixins/step';
  import Front from './step4/Front';
  import Screen from './step4/Screen';
  import Mobile from './step4/Mobile';
  import examRoom from '@/mixins/exam_room';

  export default {
    name: 'Step4',
    mixins: [stepMixin, examRoom],
    components: {
      Front,
      Screen,
      Mobile,
    },
    props: {
      exam: {
        type: Object,
      },
    },
    data() {
      return {
        return_url: null,

        phase: 3,

        webcamIsRunning: false,
        mobileIsRunning: false,
        screenIsRunning: false,

        connectChecked: {
          webcam: null,
          mobile: null,
          screen: null,
        },
      };
    },

    created() {
      this.return_url = this.$route.query?.return_url || null;
    },

    mounted() {
      const {frontMonitoring, sideMonitoring, screenMonitoring} = this.getExam;

      if (frontMonitoring) {
        this.connectStatusChanged(DEVICE_TYPE.WEBCAM, false);
      }

      if (sideMonitoring) {
        this.connectStatusChanged(DEVICE_TYPE.MOBILE, false);
      }

      if (screenMonitoring) {
        this.connectStatusChanged(DEVICE_TYPE.SCREEN_SHARE, false);
      }

      this.setPhase();
    },

    methods: {
      setPhase() {
        const {frontMonitoring, sideMonitoring, faceEvent} = this.getExam;

        if (frontMonitoring || faceEvent) {
          this.phase = CONNECT_CHECK_PHASE['WEBCAM'];
          return;
        }

        if (sideMonitoring) {
          this.phase = CONNECT_CHECK_PHASE['MOBILE'];
          return;
        }

        this.phase = CONNECT_CHECK_PHASE['SCREEN'];
      },

      connectStatusChanged(type, value) {
        this.connectChecked[type] = value;
      },

      changeFrontActive(active) {
        this.webcamIsRunning = active;
      },

      changeScreenActive(active) {
        this.screenIsRunning = active;
      },

      changeMobileActive(active) {
        this.mobileIsRunning = active;
      },

      onNext(phase) {
        if (!this.isCheckedAll) {
          const {screenMonitoring, sideMonitoring} = this.getExam;
          switch (phase) {
            case CONNECT_CHECK_PHASE['WEBCAM']:
              if (sideMonitoring) {
                this.phase = CONNECT_CHECK_PHASE['MOBILE'];
                return;
              }

              this.phase = CONNECT_CHECK_PHASE['SCREEN'];
              break;

            case CONNECT_CHECK_PHASE['MOBILE']:
              if (!screenMonitoring) return;

              this.phase = CONNECT_CHECK_PHASE['SCREEN'];
              break;

            default:
              break;
          }
          return;
        }

        if (this.getExam.renderType === EXAM_RENDER_TYPE.EXAM_WORLD_IFRAME) {
          this.renderType4();
          return;
        }

        if (this.return_url) {
          window.location.href = this.return_url;
          return;
        }

        if (
          confirm(
            this.$t('preRegistration.step4.alert.preRegistrationComplete'),
          )
        ) {
          window.close();
        }
      },

      previous() {
        const step = this.skipStep3 ? 2 : 3;
        this.$store.commit('steps/setStep', step);
      },
    },

    computed: {
      ...mapGetters({
        userInfo: 'user/getUser',
        periodInfo: 'user/getPeriod',
        getExam: 'user/getExam',
      }),

      skipStep3() {
        const pass = !this.getExam.faceEvent && !this.getExam.acceptIdCard;
        return pass;
      },

      labelButton() {
        if (!this.isCheckedAll) {
          return this.$t('preRegistration.common.nextStep');
        }

        return this.$t('preRegistration.step4.preRegistrationComplete');
      },

      // step 4 state box
      isWebcamPhase() {
        return this.phase === CONNECT_CHECK_PHASE['WEBCAM'];
      },
      isMobilePhase() {
        return this.phase === CONNECT_CHECK_PHASE['MOBILE'];
      },
      isScreenPhase() {
        return this.phase === CONNECT_CHECK_PHASE['SCREEN'];
      },

      // 현재 시험의 모든 연결체크가 완료되었는지 체크
      isCheckedAll() {
        const check = this.connectChecked;
        return (
          Object.keys(check).filter(device => {
            return check[device] !== null;
          }).length ===
          Object.keys(check).filter(device => {
            return check[device] === true;
          }).length
        );
      },

      // 다음버튼 활성화 처리
      isNextDisabled() {
        let res = true;
        switch (this.phase) {
          case CONNECT_CHECK_PHASE['WEBCAM']:
            res = this.connectChecked[DEVICE_TYPE['WEBCAM']] === false;
            break;
          case CONNECT_CHECK_PHASE['MOBILE']:
            res = this.connectChecked[DEVICE_TYPE['MOBILE']] === false;
            break;
          case CONNECT_CHECK_PHASE['SCREEN']:
            res = this.connectChecked[DEVICE_TYPE['SCREEN_SHARE']] === false;
            break;
        }
        return res;
      },

      // 이전버튼  비활성화 처리
      isPreviousDisabled() {
        let res = false;
        switch (this.phase) {
          case CONNECT_CHECK_PHASE['WEBCAM']:
            res = this.webcamIsRunning;
            break;
          case CONNECT_CHECK_PHASE['MOBILE']:
            res = this.mobileIsRunning;
            break;
          case CONNECT_CHECK_PHASE['SCREEN']:
            res = this.screenIsRunning;
            break;
        }
        return res;
      },
    },

    activated() {
      document.querySelectorAll('.ps')[0].scrollTop = 0;
      this.$emit('show-buttons', false);
    },
  };
</script>
